import { getAllTags } from '@/components/util/sdk/mattertag/data';

type Attachment = {
  originName: string;
  url: string;
};

type MatterTagAttachment = {
  id: string;
  src: string;
  type: string; // "tag.attachment.image" | "tag.attachment.video"
};

export class TagManager {
  private mpSdk: any;

  private tagAttachments: MatterTagAttachment[] = [];

  private matterTags = new Map();

  constructor(mpSdk) {
    this.mpSdk = mpSdk;
    this.init();
  }

  private init = () => {
    this.mpSdk.Tag.attachments.subscribe({
      onCollectionUpdated: (collection) => {
        this.tagAttachments = Object.values(collection);
      },
    });
  };

  subscribe = ({ mpTags, treedisTags, threads, iotDevicesWithTags }, subscriber) => {
    if (!mpTags) return subscriber(new Map());
    Object.keys(mpTags).forEach((idx) => {
      this.matterTags.set(idx, mpTags[idx]);
    });
    const matterportTags = Object.values(mpTags);
    const allTags = getAllTags(matterportTags, treedisTags, threads, iotDevicesWithTags);

    subscriber(allTags);
  };

  hasAttachment = (name: string) => {
    let matched = false;
    if (name) {
      matched = !!this.tagAttachments.find((item) => item.src.includes(name));
    }

    return matched;
  };

  getAttachment = (name: string) => {
    let matched;
    if (name) {
      matched = this.tagAttachments.find((item) => item.src.includes(name));
    }

    return matched;
  };

  addAttachment = async (attachment: Attachment, tagSid: string) => {
    try {
      const { originName, url } = attachment;
      if (this.hasAttachment(originName)) {
        return Promise.resolve();
      }
      const attachmentIds = await this.mpSdk.Tag.registerAttachment(url).catch(Promise.reject);
      if (!attachmentIds.length) {
        return Promise.resolve();
      }
      return this.mpSdk.Tag.attach(tagSid, attachmentIds[0]);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  addAttachments = async (attachments: Attachment[] = [], tagSid: string) => {
    try {
      const urls = attachments.reduce<string[]>((acc, attachment) => {
        if (!this.hasAttachment(attachment.originName)) {
          acc.push(attachment.url);
        }
        return acc;
      }, []);
      if (!urls.length) {
        return Promise.resolve();
      }
      const attachmentIds = await this.mpSdk.Tag.registerAttachment(...urls);
      if (!attachmentIds.length) {
        return Promise.resolve();
      }
      return this.mpSdk.Tag.attach(tagSid, ...attachmentIds);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  replaceAttachment = async (attachment: Attachment, newUrl: string, tagSid: string) => {
    try {
      const { originName } = attachment;
      const tagAttachment = this.getAttachment(originName);
      if (!tagAttachment) {
        this.mpSdk.Tag.detach(tagSid, tagAttachment.id).catch(Promise.reject);
      }
      const [attachmentId] = await this.mpSdk.Tag.registerAttachment(newUrl).catch(Promise.reject);
      return this.mpSdk.Tag.attach(tagSid, attachmentId);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  getMatterTags = () => {
    return this.matterTags;
  };

  getAttachments = () => {
    return this.tagAttachments;
  };
}
